// ===================================================================
// hooks/useSecureAuth.ts - HOOK D'AUTHENTIFICATION SÉCURISÉE
// ===================================================================
import { useState, useEffect, useCallback } from 'react';
import secureAuthService from '../services/secureAuthService';

interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  createdAt: string;
  lastLogin?: string;
}

interface SubscriptionStatus {
  isPremium: boolean;
  tier: 'free' | 'premium';
  status: string;
  expiresAt?: string;
  billingPeriod?: string;
  planId?: string;
}

interface UseSecureAuthReturn {
  // État d'authentification
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  
  // État d'abonnement
  subscription: SubscriptionStatus | null;
  isLoadingSubscription: boolean;
  
  // Méthodes d'abonnement
  checkAccess: (feature: string) => Promise<boolean>;
  refreshSubscription: () => Promise<void>;
}

export const useSecureAuth = (): UseSecureAuthReturn => {
  // États
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState<boolean>(false);

  // Initialisation
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Vérifier si on a déjà un token valide
        if (secureAuthService.isAuthenticated()) {
          const currentUser = secureAuthService.getCurrentUser();
          if (currentUser) {
            setUser(currentUser);
            setIsAuthenticated(true);
            
            // Charger le statut d'abonnement
            await loadSubscriptionStatus();
          }
        }
      } catch (error) {
} finally {
        setIsLoading(false);
      }
    };

    initAuth();

    // Écouter les changements d'état d'authentification
    const handleAuthChange = (event: CustomEvent) => {
      const { isAuthenticated: authStatus, user: authUser } = event.detail;
      setIsAuthenticated(authStatus);
      setUser(authUser);
      
      if (!authStatus) {
        setSubscription(null);
      } else if (authUser) {
        loadSubscriptionStatus();
      }
    };

    // Écouter les changements d'abonnement
    const handleSubscriptionChange = (event: CustomEvent) => {
    
      loadSubscriptionStatus();
    };

    window.addEventListener('authStatusChanged', handleAuthChange as EventListener);
    window.addEventListener('subscriptionUpdated', handleSubscriptionChange as EventListener);

    // Nettoyage
    return () => {
      window.removeEventListener('authStatusChanged', handleAuthChange as EventListener);
      window.removeEventListener('subscriptionUpdated', handleSubscriptionChange as EventListener);
    };
  }, []);

  // Charger le statut d'abonnement
  const loadSubscriptionStatus = async () => {
    if (!isAuthenticated) return;

    setIsLoadingSubscription(true);
    try {
      const subscriptionStatus = await secureAuthService.verifySubscription();
      setSubscription(subscriptionStatus);
    } catch (error) {
setSubscription({
        isPremium: false,
        tier: 'free',
        status: 'error'
      });
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  // ===================================================================
  // MÉTHODES D'ABONNEMENT
  // ===================================================================

  const checkAccess = useCallback(async (feature: string): Promise<boolean> => {
    if (!isAuthenticated) return false;

    try {
      const accessResult = await secureAuthService.checkAccess(feature);
      return accessResult.hasAccess;
    } catch (error) {
return false;
    }
  }, [isAuthenticated]);

  const refreshSubscription = useCallback(async () => {
    if (!isAuthenticated) return;
    await loadSubscriptionStatus();
  }, [isAuthenticated]);

  return {
    // État d'authentification
    isAuthenticated,
    user,
    isLoading,
    
    // État d'abonnement
    subscription,
    isLoadingSubscription,
    
    // Méthodes d'abonnement
    checkAccess,
    refreshSubscription
  };
};

export default useSecureAuth;